import { site, customization, integration, hooks, saving } from '@yola/ws-sdk';
import saveSite from 'src/js/modules/saving/helpers/save-site';
import getLanguageCodes from 'src/js/modules/website-settings/helpers/get-language-codes';
import migrations from 'src/js/modules/website-design/migrations';
import constants from '../constants';

const { buttonPresetMigration, colorPaletteMigration } = migrations;

const { common, emptySettings } = constants;

const initialSettingsSetup = (store) => (next) => async (action) => {
  next(action);

  if (action.type === site.actionTypes.CONNECT_SITE) {
    const state = store.getState();
    const { userData } = integration.selectors.getSettings(state) || {};
    const isInitialSettingsApplied = site.selectors.getInitialSettingsApplied(state);
    const isSiteSetupCompleted = site.selectors.getInitialSetupCompleted(state);
    let shouldSaveSite = false;

    if (!isInitialSettingsApplied && !isSiteSetupCompleted) {
      shouldSaveSite = true;

      /**
       * We can not update globals right away because globals have not downloaded yet.
       * To be sure that globals are ready we use `ON_BEFORE_PAGE_LOADED` hook.
       */
      hooks.operations.subscribe(
        hooks.availableHooks.ON_BEFORE_PAGE_LOADED,
        () => {
          const pages = site.selectors.getPages(state);
          const activePageId = site.selectors.getActivePageId(state);
          const settings = site.selectors.getSettings(state);
          const currentSiteName = site.selectors.getSiteName(state);
          const { email: userEmail, is_white_label: isWL } = userData;
          const businessEmails = isWL ? [] : [userEmail];

          const settingsToSave = {
            ...emptySettings,
            ...settings,
            businessName: settings?.businessName || currentSiteName,
            siteName: settings?.siteName || currentSiteName,
            emails: settings?.emails || businessEmails,
          };
          const updateGlobals = true;
          const updateCopyrightGlobal = true;
          store.dispatch(
            site.actions.initSettingsForFirstLoading(
              settingsToSave,
              updateGlobals,
              updateCopyrightGlobal
            )
          );
          store.dispatch(
            site.actions.setSiteProperty(common.INITIAL_SETTINGS_APPLIED_PROPERTY_NAME, true)
          );

          const languageCodes = getLanguageCodes();

          pages.forEach((page) => {
            if (site.verifiers.isHomePage(page.path, languageCodes)) return;
            if (page.id === activePageId) return;
            store.dispatch(site.actions.deletePage(page.id, languageCodes));
          });
        },
        { once: true }
      );
    }

    const colorPaletteConfigUrl = customization.selectors.getColorPaletteConfigAssetUrl(state);
    if (colorPaletteConfigUrl && !customization.selectors.getColorPaletteConfig(state)) {
      try {
        const { data } = await site.helpers.fetchAsset(colorPaletteConfigUrl);
        store.dispatch(customization.actions.setColorPaletteConfig(data));
      } catch (error) {
        console.error('Failed to load color palette config:', error);
      }
    }

    const isButtonPresetMigrated = buttonPresetMigration(store);
    const isColorPaletteMigrated = colorPaletteMigration(store);

    if (isButtonPresetMigrated || isColorPaletteMigrated) {
      shouldSaveSite = true;
      store.dispatch(saving.actions.setStatus(saving.constants.statuses.UNSAVED));
    }

    if (shouldSaveSite) {
      /**
       * We can not save changes right away
       * it is too early and editor has not ready yet,
       * so that we save changes when document is ready
       */
      hooks.operations.subscribe(
        hooks.availableHooks.ON_LIVE_DOCUMENT_CONNECTED,
        () => {
          saveSite();
        },
        { once: true }
      );
    }
  }
};

export default initialSettingsSetup;
